<template>
  <tr>
    <td class="main-td">
      <div>
        <RouterLink
          :to="{name: 'vacancy', params: { id: vacancy.id }}"
          class="vacancy-name-link"
        >
          {{ vacancy.title }}
        </RouterLink>
      </div>
    </td>
    <td>
      <span class="d-md-none">{{ headers[1].text }}: </span>
      {{ creatorName }}
    </td>
    <td>
      <span class="d-md-none">{{ headers[2].text }}</span>
      {{ city }}
    </td>
    <td>
      <div>
        <RouterLink :to="{name: 'candidates', params: { id: vacancy.id }}">
          <span class="d-md-none">{{ headers[3].text }}</span>
          {{ vacancy.counts.candidates }}
        </RouterLink>
      </div>
      <RouterLink
        :to="{name: 'candidates', params: { id: vacancy.id }}"
        class="caption hover-show"
      >
        {{ $t('watchCandidates') }}
      </RouterLink>
    </td>
    <td>
      <div>
        <span class="d-md-none">{{ headers[4].text }}</span>
        {{ vacancy.counts.interviews }}
        <span
          v-if="vacancy.counts.interviewsNew"
          class="red--text"
        >
          (+{{ vacancy.counts.interviewsNew }})
        </span>
      </div>
    </td>
    <td>
      <span class="d-md-none">{{ headers[5].text }}: </span>{{ hasTestText }}
    </td>
    <td class="text-left">
      <span class="d-md-none">{{ headers[6].text }}</span>
      {{ archivedAt }}
    </td>
    <td class="text-right menu-td">
      <VProgressCircular
        v-if="actionLoading"
        indeterminate
        color="blue"
        :size="30"
        :width="3"
      />
      <!--<div v-else class="text-left caption d-inline-block">
        <div>
          <a href="#" @click.prevent="restore">{{ $t('restore') }}</a>
        </div>
        <div>
          <a href="" @click.prevent="clone">{{ $t('copy') }}</a>
        </div>
      </div>-->

      <VMenu
        class="table-menu"
        bottom
        left
      >
        <template v-slot:activator="{ on, attrs }">
          <VBtn
            icon
            v-bind="attrs"
            v-on="on"
          >
            <VIcon color="#757575">
              mdi-dots-vertical
            </VIcon>
          </VBtn>
        </template>
        <VList class="table-list">
          <VListItem class="table-list-item">
            <a
              href="#"
              @click.prevent="restore"
              :class="{'disabled-link': isDisabledLink}"
              :aria-disabled="isDisabledLink"
            >
              {{ $t('restore') }}
            </a>
          </VListItem>
          <VListItem class="table-list-item">
            <a
              href="#"
              @click.prevent="clone"
              :class="{'disabled-link': isDisabledLink}"
              :aria-disabled="isDisabledLink"
            >
              {{ $t('copy') }}
            </a>
          </VListItem>
        </VList>
      </VMenu>
    </td>
  </tr>
</template>

<script>
import useUser from '@/compositions/useUser';
import { safeGet } from '../../functions';
import ValidationMixin from '../../mixins/ValidationMixin';
import { DATE_FORMATS, format } from '../../date-utils';
import Vacancy from '../../orm/models/Vacancy/Vacancy';
import useSubscription from '@/compositions/accounting/useSubscription';

export default {
  name: 'VacancyArchiveTableRow',
  mixins: [ValidationMixin],
  props: {
    vacancy: {
      type: Object,
      required: true,
    },
    headers: {
      type: Array,
      required: true,
    },
    limitedVacancies: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  data() {
    return {
      actionLoading: false,
    };
  },
  setup() {
    const { getUsers } = useUser();

    const { isExpired } = useSubscription()

    return {
      getUsers,
      isExpired
    };
  },
  computed: {
    city() {
      return safeGet(this.vacancy.address, ['city']);
    },
    archivedAt() {
      if (this.vacancy.archivedAt === null) {
        return null;
      }

      return format(new Date(this.vacancy.archivedAt), DATE_FORMATS[this.$i18n.locale].short);
    },
    creatorName() {
      return safeGet(this.vacancy, ['creatorName']);
    },
    hasTestText() {
      return this.vacancy.hasTest ? this.$t('yes') : this.$t('no');
    },
    isDisabledLink() {
      return this.vacancy.isAiInterviewCompiling
      || this.vacancy.isAiInterviewRecompiling
      || this.vacancy.aiQuestionsGenerating;
    },
  },
  methods: {
    async restore() {
      const beforeDestroy = () => {
        this.$toast.info(this.$t('vacancyRestored'));
      };
      await this.withApiErrorHandling(async () => {
        if (this.isExpired) {
          this.$emit('openDialogExpiredTariff');
          return;
        }
        if (this.limitedVacancies) {
          this.$emit('openDialogMaximumVacancies', 'restore');
          return;
        }
        this.$on('hook:beforeDestroy', beforeDestroy);
        await Vacancy.api().restore(this.vacancy.id);
        await this.getUsers();
      }, (e) => {
        this.$off('hook:beforeDestroy', beforeDestroy);
        this.$toast.error(e.message);
      }, 'actionLoading');
    },
    async clone() {
      await this.withApiErrorHandling(async () => {
        if (this.isExpired) {
          this.$emit('openDialogExpiredTariff');
          return;
        }
        if (this.limitedVacancies) {
          this.$emit('openDialogMaximumVacancies', 'clone');
          return;
        }
        await Vacancy.api().clone(this.vacancy.id);
        this.$toast.info(this.$t('vacancyCloned'));
      }, (e) => this.$toast.error(e.message), 'actionLoading');
    },
  },
};
</script>

<style lang="scss" scoped>
.disabled-link {
  color: #ccc;
  pointer-events: none;
  cursor: not-allowed;
}
</style>

<i18n locale="en" lang="yaml">
  watchCandidates: Watch candidates
  watchInterview: Watch interviews
  restore: Restore
  copy: Copy
  vacancyRestored: Job restored
  vacancyCloned: Job copied
  yes: Yes
  no: No
</i18n>

<i18n locale="ru" lang="yaml">
  watchCandidates: Смотреть кандидатов
  watchInterview: Смотреть интервью
  restore: Восстановить
  copy: Создать копию
  vacancyRestored: Вакансия восстановлена
  vacancyCloned: Вакансия скопирована
  yes: Да
  no: Нет
</i18n>
